import { matchPath } from "react-router-dom";
import { makeUrl } from "src/routes/makeUrl";
import { PageUrl } from "src/routes/PageUrl";
import { ZMenuItem } from "./ZMenuItem";
import { makeMenuItemKey } from "./makeMenuItemKey";
import { getFilterRestrictionQuery } from "./getFilterRestrictionQuery";

export type ItemType = {
  key: string;
  pageUrl?: string;
  searchParams: URLSearchParams;
};

const areSearchParamsEqual = (
  targetParams: URLSearchParams,
  currentParams: URLSearchParams,
) => {
  const targetEntries = Array.from(targetParams.entries());
  const currentEntries = Array.from(currentParams.entries());

  return (
    targetEntries.length === currentEntries.length &&
    targetEntries.every(([key, value]) => currentParams.get(key) === value)
  );
};

const isActiveUrl = (
  pageUrl: string,
  pageSearchParams: URLSearchParams,
  pathname: string,
  search: string,
) =>
  Boolean(matchPath({ path: pageUrl }, pathname)) &&
  areSearchParamsEqual(pageSearchParams, new URLSearchParams(search));

const flattenItems = (
  items: ZMenuItem[] | null | undefined,
): ItemType[] | undefined =>
  items?.flatMap(({ link, elements, id }: ZMenuItem) => {
    const [pageUrl, searchParams] = (() => {
      switch (link.type) {
        case "internal":
          return [makeUrl(link.url as PageUrl), new URLSearchParams()];
        case "object":
          return [
            makeUrl(PageUrl.entities, { objectId: link.objectId }),
            new URLSearchParams(getFilterRestrictionQuery(link)),
          ];
        default:
          return [undefined, new URLSearchParams()];
      }
    })();
    if (!pageUrl) return flattenItems(elements) ?? [];
    const flatItem: ItemType = {
      key: makeMenuItemKey(id),
      pageUrl,
      searchParams,
    };
    const flattened = elements ? (flattenItems(elements) ?? []) : [];
    return [flatItem, ...flattened];
  });

export const findActiveItem = (
  list: ZMenuItem[],
  pathname: string,
  search = "",
) => {
  const items = flattenItems(list);
  return items?.find(({ pageUrl, searchParams }) =>
    isActiveUrl(pageUrl ?? "", searchParams, pathname, search),
  );
};
